import { useState, useEffect, createContext, useContext } from "react";

const UserContext = createContext();
const TELEGRAM_INIT_RETRY_MS = 200;
const TELEGRAM_INIT_MAX_ATTEMPTS = 10;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function getTelegramWebAppWithInitData() {
  if (typeof window === "undefined") {
    return null;
  }

  for (let attempt = 0; attempt < TELEGRAM_INIT_MAX_ATTEMPTS; attempt += 1) {
    const tg = window.Telegram?.WebApp;

    if (tg) {
      tg.ready?.();

      if (tg.initData) {
        return tg;
      }
    }

    await wait(TELEGRAM_INIT_RETRY_MS);
  }

  return window.Telegram?.WebApp || null;
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const authUser = async () => {
      setLoading(true);
      setError(null);

      try {
        const tg = await getTelegramWebAppWithInitData();

        if (isCancelled) {
          return;
        }

        if (tg?.initData) {
          const response = await fetch("/api/users/auth", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Telegram-Init-Data": tg.initData,
            },
            body: JSON.stringify({ initData: tg.initData }),
          });

          if (!response.ok) {
            let errorMessage = `Authentication failed (${response.status})`;

            try {
              const errorData = await response.json();
              if (errorData?.error) {
                errorMessage = errorData.error;
              }
            } catch {
              // Ignore JSON parsing errors for non-JSON responses.
            }

            throw new Error(errorMessage);
          }

          const data = await response.json();

          if (!data.user) {
            throw new Error("User data not found in response");
          }

          if (isCancelled) {
            return;
          }

          setUser(data.user);
          localStorage.setItem("cgpeers_user", JSON.stringify(data.user));
          return;
        }

        if (import.meta.env.DEV) {
          const devUser = {
            id: 5472743372,
            first_name: "Admin",
            username: "Cgpeers_invites",
            is_admin: true,
            access_granted: true,
            telegram_id: 5472743372,
          };

          setUser(devUser);
          localStorage.setItem("cgpeers_user", JSON.stringify(devUser));
          return;
        }

        throw new Error("Telegram WebApp initData is unavailable");
      } catch (authError) {
        if (isCancelled) {
          return;
        }

        console.error("User authentication error:", authError);
        setUser(null);
        localStorage.removeItem("cgpeers_user");
        setError(authError.message);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    authUser();

    return () => {
      isCancelled = true;
    };
  }, []);

  // Активен ли премиум:
  // access_granted=false                          -> нет
  // access_granted=true, premium_expires_at=NULL  -> lifetime (legacy / ручной бессрочный)
  // access_granted=true, premium_expires_at>now   -> активная подписка
  const isPremiumActive = (u) => {
    if (!u?.access_granted) return false;
    if (!u.premium_expires_at) return true;
    return new Date(u.premium_expires_at) > new Date();
  };

  const hasAccess = () => {
    return isPremiumActive(user);
  };

  const isAdmin = () => {
    return user?.is_admin || false;
  };

  const isBanned = () => {
    return user?.is_banned || false;
  };

  const isTester = () => {
    return user?.is_tester || false;
  };

  const canAccessProfile = () => {
    return true;
  };

  const value = {
    user,
    loading,
    error,
    hasAccess,
    isAdmin,
    isBanned,
    isTester,
    canAccessProfile,
    setUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
